import { Type, plainToInstance } from 'class-transformer'
import { Entity } from '../entity'
import { ISiteBoxDocumentConfig } from './ISiteBoxDocumentConfig'
import { ISiteBoxDocumentConfigConfig } from './ISiteBoxDocumentConfigConfig'
import { SiteBoxDocumentConfigResult } from './SiteBoxDocumentConfigResult'
import { ISiteBoxDocumentConfigResultOutput } from './ISiteBoxDocumentConfigResultOutput'
import moment from 'moment'
import { SiteBoxDocumentConfigStatus } from './types'
import { localizedStrings } from '@core/strings'

export class SiteBoxDocumentConfig extends Entity<ISiteBoxDocumentConfig> {
  id: number
  organisationId: number
  projectId: number
  documentId: number
  config: ISiteBoxDocumentConfigConfig

  @Type(() => SiteBoxDocumentConfigResult)
  result: SiteBoxDocumentConfigResult[]
  createdBy: number
  modifiedBy: number
  userId: number
  status: SiteBoxDocumentConfigStatus
  expiryDate?: string

  static readonly EXPIRY_AMBER_THRESHOLD_DAYS = 7

  public static new(payload: ISiteBoxDocumentConfig) {
    const entity = plainToInstance(SiteBoxDocumentConfig, payload)

    return entity
  }

  public static removeEmptyQueries(
    config: ISiteBoxDocumentConfigConfig,
  ): ISiteBoxDocumentConfigConfig {
    return {
      ...config,
      queries: config.queries.filter((query) => query.query.length > 0),
    }
  }

  public removeEmptyQueries(): ISiteBoxDocumentConfigConfig {
    return SiteBoxDocumentConfig.removeEmptyQueries(this.config)
  }

  public isDocumentAppraised(): boolean {
    return this.status === 'PASS' || this.status === 'FAIL'
  }

  public areAllQueriesAppraised(): boolean {
    return this.result.every((r) => r.isAppraised())
  }

  public getAppraisalStatusText(): string {
    if (this.status === 'PASS') {
      return localizedStrings.pass
    }
    if (this.status === 'FAIL') {
      return localizedStrings.fail
    }
    if (this.status === 'IN_REVIEW') {
      return localizedStrings.inReview
    }
    return localizedStrings.never
  }

  public hasExpiry(): boolean {
    return !!this.expiryDate
  }

  public isExpired(): boolean {
    if (!this.expiryDate) {
      return false
    }
    return moment(this.expiryDate).isBefore(moment())
  }

  public getDaysUntilExpiry(): number | null {
    if (!this.expiryDate) {
      return null
    }
    return moment(this.expiryDate).diff(moment(), 'days')
  }

  public getExpiryStatusString(): string | null {
    if (!this.expiryDate) {
      return null
    }
    if (this.isExpired()) {
      return localizedStrings.expired
    }
    return localizedStrings.expires
  }

  public getExpiryStatusTextColor():
    | 'success.main'
    | 'warning.main'
    | 'error.main'
    | null {
    if (!this.expiryDate || this.status !== 'PASS') {
      return null
    }
    if (this.isExpired()) {
      return 'error.main'
    }
    const days = this.getDaysUntilExpiry() ?? 0
    if (days <= SiteBoxDocumentConfig.EXPIRY_AMBER_THRESHOLD_DAYS) {
      return 'warning.main'
    }
    return 'success.main'
  }

  public getFormattedExpiryDate(): string | null {
    if (!this.expiryDate) {
      return null
    }
    return moment(this.expiryDate).format('DD/MM/YYYY')
  }

  public getExpiryReviewLabel(): string | null {
    if (!this.expiryDate || this.status !== 'PASS') {
      return null
    }
    const formattedExpiryDate = this.getFormattedExpiryDate()
    const expireStatus = this.getExpiryStatusString()
    return `${expireStatus} ${formattedExpiryDate}`
  }

  public getExpirySelectorSuffix(): string | null {
    if (this.status === 'PASS') {
      if (!this.expiryDate) {
        return localizedStrings.passed
      }
      const formattedExpiryDate = this.getFormattedExpiryDate()
      const expireStatus = this.getExpiryStatusString()
      return `${localizedStrings.passed} — ${expireStatus} ${formattedExpiryDate}`
    }
    if (this.status === 'FAIL') {
      return localizedStrings.failed
    }
    return null
  }

  // Helper function to get the populated field value and format it for display
  public static getDisplayValue = (
    output: ISiteBoxDocumentConfigResultOutput,
  ): string => {
    const CURRENCY_SYMBOLS = [
      '¤',
      '$',
      '¢',
      '£',
      '€',
      '¥',
      '₹',
      '₩',
      '₽',
      '₴',
      '₦',
      '₱',
      '₫',
      '₭',
      '₮',
      '₲',
      '₪',
      '₡',
      '₵',
      '₸',
      '₼',
      '៛',
      '₨',
      'ƒ',
      '圓',
      '元',
    ]

    if (output.singleValue) {
      return output.singleValue
    }

    if (output.dateTimeList.length > 0) {
      return output.dateTimeList
        .map((date) => moment(date).format('DD MMM YYYY'))
        .join('\n')
    }

    if (output.numberList.length > 0) {
      if (output.numberList[0].unit) {
        return output.numberList
          .map((item) =>
            CURRENCY_SYMBOLS.includes(item.unit)
              ? `${item.unit} ${item.value}`
              : `${item.value} ${item.unit}`,
          )
          .join('\n')
      } else {
        return output.numberList.map((item) => item.value).join(', ')
      }
    }

    if (output.stringList.length > 0) {
      return output.stringList.map((item) => item).join(', ')
    }

    return ''
  }
}
